import { getImageDimensions } from '@/utils/image';
import { isArray, isInteger, isNumber, isString } from 'lodash';

/**
 * Checks if given value has given dimensions.
 *
 * @param   {*}            value            Value to test.
 * @param   {object}       config           Check configuration.
 * @param   {number|null}  config.minWidth  Minimum width of file.
 * @param   {number|null}  config.minHeight Minimum height of file.
 * @param   {number|null}  config.maxWidth  Maximum width of file.
 * @param   {number|null}  config.maxHeight Maximum height of file.
 * @param   {number|null}  config.ratio     Ratio of file.
 * @returns {Promise}                       Check result.
 */
export const isDimensions = async (value, {
    minWidth = null,
    minHeight = null,
    maxWidth = null,
    maxHeight = null,
    ratio = null,
}) => {
    if (! isFile(value)) {
        return false;
    }

    const { width, height } = await getImageDimensions(value);

    return (! isNumber(ratio) || width / height === ratio) &&
        (! isNumber(minWidth) || width >= minWidth) &&
        (! isNumber(minHeight) || height >= minHeight) &&
        (! isNumber(maxWidth) || width <= maxWidth) &&
        (! isNumber(maxHeight) || height <= maxHeight);
};

/**
 * Checks if given value is file.
 *
 * @param   {*}       value  Value to test.
 * @returns {boolean}        Check result.
 */
export const isFile = (value) => {
    return value instanceof File;
};

/**
 * Checks if given value is filled.
 *
 * @param   {*}       value  Value to test.
 * @returns {boolean}        Check result.
 */
export const isFilled = (value) => {
    if (isString(value) || isArray(value)) {
        return 0 < value.length;
    }

    return !! value;
};

/**
 * Checks if given value is in given array.
 *
 * @param   {*}       value       Value to test.
 * @param   {*}       collection  Collection with values.
 * @returns {boolean}             Check result.
 */
export const isIn = (value, collection) => {
    return (isString(collection) || isArray(collection)) && collection.includes(value);
};

/**
 * Checks if given value is less or equal given max value.
 *
 * For strings and arrays it checks maximum length of values.
 * For files it checks size.
 *
 * @param   {*}       value  Value to test.
 * @param   {*}       max    Maximum value.
 * @returns {boolean}        Check result.
 */
export const isMax = (value, max) => {
    if (! isNumber(max)) {
        return false;
    }

    if (isString(value) || isArray(value)) {
        return value.length <= max;
    }

    if (isFile(value)) {
        return value.size <= max;
    }

    return true;
};

/**
 * Checks if given file has allowed mime type.
 *
 * @param   {*}        file       Value to test.
 * @param   {*}        mimeTypes  Allowed mime types.
 * @returns {boolean}             Check result.
 */
export const isMimeType = (file, mimeTypes) => {
    if (! isFile(file) || ! isArray(mimeTypes)) {
        return false;
    }

    return isIn(file.type, mimeTypes);
};

/**
 * Checks if given number is integer-like.
 *
 * @example isIntegerLike(1) // true
 * @example isIntegerLike('1') // true
 * @example isIntegerLike('test') // false
 * @example isIntegerLike(1.0) // true
 * @example isIntegerLike(1.5) // false
 *
 * @param   {*}       number Value to check.
 * @returns {boolean}        Check result.
 */
export const isIntegerLike = (number) => {
    if (isInteger(number)) {
        return true;
    }

    return ! isNaN(number) && 0 === number % 1;
};

/**
 * Checks whether given value is valid URL.
 *
 * @param  {*} value Value to check
 * @returns {boolean}     Check result.
 */
export const isUrl = (value) => {
    if (! isString(value)) {
        return false;
    }

    try {
        const url = new URL(value);

        if (! ['http:', 'https:'].includes(url.protocol)) {
            return false;
        }
    } catch (e) {
        return false;
    }

    return true;
};
